/**
 * Which CaseBlink starter automations to show under Explore on the Case
 * Automations board. Starters are grouped by practice area. When the case
 * has a visa, the ones written for that visa come first as their own group.
 */
import {
  STARTER_CATEGORIES,
  STARTER_SKILLS,
  type StarterCategory,
  type StarterSkill
} from "@/features/automations/components/skillModel";
import { skillRunLine } from "@/features/firmKnowledge/skillRunText";
import { canonicalVisa } from "@/features/firmKnowledge/visaCatalog";

export type ExploreGroup = {
  title: string;
  starters: StarterSkill[];
};

const visaKey = (visa: string): string =>
  (canonicalVisa(visa) ?? visa).trim().toLowerCase();

/** Visas this starter is offered for on a case; empty means any case. */
export const starterVisaTypes = (starter: StarterSkill): string[] => {
  const fromStarter = starter.visa_types ?? [];
  const fromTrigger = starter.trigger?.visa_types ?? [];
  return [...new Set([...fromStarter, ...fromTrigger])];
};

/** Whether a starter belongs in Explore for this case's visa. */
export const starterMatchesCaseVisa = (
  starter: StarterSkill,
  caseVisa: string | null | undefined
): boolean => {
  const scoped = starterVisaTypes(starter);
  if (!scoped.length) return true;
  if (!caseVisa?.trim()) return false;
  const key = visaKey(caseVisa);
  return scoped.some((v) => visaKey(v) === key);
};

const byCategory = (starters: readonly StarterSkill[]): ExploreGroup[] =>
  STARTER_CATEGORIES.map((category: StarterCategory) => ({
    title: category,
    starters: starters.filter((s) => s.category === category)
  })).filter((g) => g.starters.length > 0);

/**
 * What Casey is asked when an Explore automation is run on this case. The
 * steps travel with the message because a starter is not a saved firm skill.
 */
export const starterRunMessage = (
  starter: Pick<StarterSkill, "title" | "instructions">
): string => `${skillRunLine(starter.title)}\n\n${starter.instructions}`;

/** Explore groups for the Case Automations board. */
export const exploreGroupsForCase = (
  caseVisa: string | null | undefined
): ExploreGroup[] => {
  const visa = caseVisa?.trim() || null;
  const eligible = STARTER_SKILLS.filter((s) =>
    starterMatchesCaseVisa(s, visa)
  );
  if (!visa) return byCategory(eligible);

  const general = eligible.filter((s) => !starterVisaTypes(s).length);
  const forVisa = eligible.filter((s) => starterVisaTypes(s).length);
  const label = canonicalVisa(visa) ?? visa;
  const groups: ExploreGroup[] = [];
  if (forVisa.length) {
    groups.push({ title: `${label} Automations`, starters: forVisa });
  }
  groups.push(...byCategory(general));
  return groups;
};
